import { auth, storage } from '../firebase/config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { logger } from './logger';

export interface StorageDiagnosticResult {
  success: boolean;
  error?: string;
  details?: any;
}

export const runStorageDiagnostic = async (): Promise<StorageDiagnosticResult> => {
  try {
    logger.info('StorageDiagnostic', 'Iniciando diagnóstico de Firebase Storage');

    // 1. Verificar autenticación
    if (!auth.currentUser) {
      return {
        success: false,
        error: 'Usuario no autenticado',
        details: { step: 'authentication' }
      };
    }

    const userId = auth.currentUser.uid;
    logger.info('StorageDiagnostic', 'Usuario autenticado:', userId);

    // 2. Verificar token
    let token;
    try {
      token = await auth.currentUser.getIdToken(true);
      if (!token) {
        return {
          success: false,
          error: 'Token de autenticación inválido',
          details: { step: 'token_validation' }
        };
      }
      logger.info('StorageDiagnostic', 'Token válido obtenido');
    } catch (tokenError) {
      return {
        success: false,
        error: 'Error al obtener token',
        details: { step: 'token_validation', error: tokenError }
      };
    }

    // 3. Verificar configuración de Storage
    try {
      ref(storage, 'test');
      logger.info('StorageDiagnostic', 'Configuración de Storage válida');
    } catch (storageError) {
      return {
        success: false,
        error: 'Error en configuración de Storage',
        details: { step: 'storage_config', error: storageError }
      };
    }

    // 4. Probar subida de archivo de prueba
    const testFileName = `diagnostic_test_${Date.now()}.txt`;
    const testRef = ref(storage, `users/${userId}/certifications/${testFileName}`);
    const testContent = 'Archivo de prueba para diagnóstico - ' + new Date().toISOString();
    const testBlob = new Blob([testContent], { type: 'text/plain' });

    try {
      logger.info('StorageDiagnostic', 'Intentando subir archivo de prueba:', testFileName);
      logger.info('StorageDiagnostic', 'Ruta del archivo:', `users/${userId}/certifications/${testFileName}`);
      logger.info('StorageDiagnostic', 'Tamaño del archivo: ' + testBlob.size + ' bytes');
      
      const uploadResult = await uploadBytes(testRef, testBlob);
      logger.info('StorageDiagnostic', 'Archivo de prueba subido exitosamente:', uploadResult);

      // 5. Probar descarga del archivo
      const downloadUrl = await getDownloadURL(testRef);
      logger.info('StorageDiagnostic', 'URL de descarga obtenida:', downloadUrl);

      // 6. Limpiar archivo de prueba
      await deleteObject(testRef);
      logger.info('StorageDiagnostic', 'Archivo de prueba eliminado');

      return {
        success: true,
        details: {
          userId,
          testFileName,
          downloadUrl,
          uploadResult,
          message: 'Diagnóstico completado exitosamente',
          timestamp: new Date().toISOString()
        }
      };
    } catch (uploadError: any) {
      logger.error('StorageDiagnostic', 'Error en operaciones de Storage:', uploadError);
      
      // Análisis detallado del error
      let errorAnalysis: any = {
        step: 'storage_operations',
        error: uploadError,
        errorCode: uploadError.code,
        errorMessage: uploadError.message,
        userId,
        testFileName,
        filePath: `users/${userId}/certifications/${testFileName}`,
        fileSize: testBlob.size,
        hasToken: !!token,
        timestamp: new Date().toISOString()
      };

      // Agregar información específica según el tipo de error
      if (uploadError.code === 'storage/unauthorized') {
        errorAnalysis.errorType = 'PERMISSION_DENIED';
        errorAnalysis.suggestion = 'Verificar reglas de Storage y autenticación del usuario';
      } else if (uploadError.code === 'storage/unauthenticated') {
        errorAnalysis.errorType = 'UNAUTHENTICATED';
        errorAnalysis.suggestion = 'Usuario no autenticado correctamente';
      } else if (uploadError.code === 'storage/quota-exceeded') {
        errorAnalysis.errorType = 'QUOTA_EXCEEDED';
        errorAnalysis.suggestion = 'Cuota de Storage excedida';
      }

      return {
        success: false,
        error: uploadError.message || 'Error desconocido en Storage',
        details: errorAnalysis
      };
    }
  } catch (error: any) {
    logger.error('StorageDiagnostic', 'Error general en diagnóstico:', error);
    return {
      success: false,
      error: error.message || 'Error general en diagnóstico',
      details: { 
        step: 'general', 
        error,
        timestamp: new Date().toISOString()
      }
    };
  }
};

export const checkStoragePermissions = async (): Promise<StorageDiagnosticResult> => {
  return await runStorageDiagnostic();
}; 